/**
 * Date Formatting Utility
 * 
 * Best Practice: Store timestamps in UTC, display in user's local timezone
 * This utility handles the conversion for display purposes
 */

/**
 * Format a date/timestamp for display in the user's local timezone
 * @param {string|Date|Object} value - The date value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time (default: true)
 * @param {boolean} options.includeSeconds - Whether to include seconds (default: false)
 * @param {string} options.dateStyle - 'short', 'medium', 'long', 'full' (default: 'medium')
 * @returns {string} - Formatted date string in user's local timezone
 */
export const formatDateTime = (value, options = {}) => {
  const {
    includeTime = true,
    includeSeconds = false,
    dateStyle = 'medium'
  } = options;

  if (!value) return '—';

  try {
    let date;

    // Handle Firestore Timestamp objects
    if (value && typeof value === 'object' && value.toDate) {
      date = value.toDate();
    }
    // Handle Firestore server timestamp format { seconds, nanoseconds }
    else if (value && typeof value === 'object' && value.seconds) {
      date = new Date(value.seconds * 1000);
    }
    // Handle ISO string or other date strings
    else if (typeof value === 'string') {
      date = new Date(value);
    }
    // Handle Date objects
    else if (value instanceof Date) {
      date = value;
    }
    // Handle Unix timestamp (milliseconds)
    else if (typeof value === 'number') {
      date = new Date(value);
    }
    else {
      return String(value);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(value);
    }

    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Format options based on dateStyle
    const dateOptions = {
      short: { month: 'numeric', day: 'numeric', year: '2-digit' },
      medium: { month: 'short', day: 'numeric', year: 'numeric' },
      long: { month: 'long', day: 'numeric', year: 'numeric' },
      full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
    };

    const formatOptions = {
      ...dateOptions[dateStyle],
      timeZone: userTimezone
    };

    if (includeTime) {
      formatOptions.hour = 'numeric';
      formatOptions.minute = '2-digit';
      if (includeSeconds) {
        formatOptions.second = '2-digit';
      }
      formatOptions.hour12 = true;
    }

    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(value);
  }
};

/**
 * Format a date only (no time) for display
 * @param {string|Date|Object} value - The date value to format
 * @param {string} dateStyle - 'short', 'medium', 'long', 'full' (default: 'medium')
 * @returns {string} - Formatted date string
 */
export const formatDate = (value, dateStyle = 'medium') => {
  return formatDateTime(value, { includeTime: false, dateStyle });
};

/**
 * Format time only for display
 * @param {string|Date|Object} value - The date value to format
 * @param {boolean} includeSeconds - Whether to include seconds
 * @returns {string} - Formatted time string
 */
export const formatTime = (value, includeSeconds = false) => {
  if (!value) return '—';

  try {
    let date;

    if (value && typeof value === 'object' && value.toDate) {
      date = value.toDate();
    } else if (value && typeof value === 'object' && value.seconds) {
      date = new Date(value.seconds * 1000);
    } else if (typeof value === 'string') {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      return String(value);
    }

    if (isNaN(date.getTime())) {
      return String(value);
    }

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    };

    if (includeSeconds) {
      formatOptions.second = '2-digit';
    }

    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (error) {
    console.error('Error formatting time:', error);
    return String(value);
  }
};

/**
 * Check if a field name indicates it's a date/timestamp field
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if it's a date field
 */
export const isDateField = (fieldName) => {
  if (!fieldName) return false;
  
  const lowerFieldName = fieldName.toLowerCase().replace(/[\s_]/g, '');
  
  // Explicitly exclude fields that are NOT dates
  const excludedFields = [
    'patientid', 'patientidentifier', 'userid', 'procedureid',
    'documentid', 'recordid', 'platform', 'state', 'status', 
    'category', 'location', 'update', 'updated'
  ];
  
  if (excludedFields.some(excluded => lowerFieldName === excluded)) {
    return false;
  }
  
  // Specific date field names to match (be very explicit)
  const exactDateFields = [
    'date', 'datetime', 'time', 'timestamp',
    'createdat', 'updatedat', 'submittedat', 'completedat', 
    'occurredat', 'expiresat', 'startedat', 'endedat',
    'createdtime', 'updatedtime', 'submittedtime',
    'completiondate', 'submissiondate', 'eventdate', 'incidentdate',
    'dateofbirth', 'dob', 'birthdate', 'expirationdate', 'expiration',
    'dateofevent', 'starttime', 'endtime', 'startdate', 'enddate'
  ];
  
  // Check for exact match
  if (exactDateFields.includes(lowerFieldName)) {
    return true;
  }
  
  // Check if field ends with 'date', 'time', or 'at' (for timestamps like submittedAt)
  if (lowerFieldName.endsWith('date') || 
      lowerFieldName.endsWith('time') || 
      (lowerFieldName.endsWith('at') && lowerFieldName.length > 4)) {
    // Extra check: make sure it's not 'format', 'that', etc.
    const validEndings = ['submittedat', 'createdat', 'updatedat', 'completedat', 'startedat', 'endedat', 'occurredat'];
    if (lowerFieldName.endsWith('at')) {
      return validEndings.some(ending => lowerFieldName.endsWith(ending));
    }
    return true;
  }
  
  return false;
};

/**
 * Check if a value looks like a date/timestamp
 * @param {any} value - The value to check
 * @returns {boolean} - True if it looks like a date
 */
export const isDateValue = (value) => {
  if (!value) return false;

  // Firestore Timestamp object
  if (typeof value === 'object' && (value.toDate || value.seconds)) {
    return true;
  }

  // ISO 8601 string pattern (e.g., "2026-01-15T15:51:54.813Z")
  if (typeof value === 'string') {
    // Check for ISO format with time
    const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoPattern.test(value)) {
      return true;
    }
    
    // Check for date-only format (YYYY-MM-DD)
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyPattern.test(value)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if a value is a date-only string (no time component)
 * @param {any} value - The value to check
 * @returns {boolean} - True if it's a date-only string
 */
export const isDateOnlyValue = (value) => {
  if (!value || typeof value !== 'string') return false;
  // YYYY-MM-DD format without time
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

/**
 * Get the user's current timezone name
 * @returns {string} - Timezone name (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get a human-readable timezone abbreviation
 * @returns {string} - Timezone abbreviation (e.g., "EST", "PST")
 */
export const getTimezoneAbbreviation = () => {
  const date = new Date();
  const timeString = date.toLocaleTimeString('en-US', { timeZoneName: 'short' });
  // Extract timezone abbreviation from string like "10:30:00 AM EST"
  const parts = timeString.split(' ');
  return parts[parts.length - 1];
};

export default {
  formatDateTime,
  formatDate,
  formatTime,
  isDateField,
  isDateValue,
  isDateOnlyValue,
  getUserTimezone,
  getTimezoneAbbreviation
};
